/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography, useTheme } from '@zapier/design-system';

type Props = {
  /**
   * The base URL after which all of the letters render.
   */
  baseUrl: string;
  /**
   * The text to render above the alphabetic navigation
   */
  title: string;
};

const Styles = {
  root: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    justify-items: center;
    ${Typography.smallPrint3};
    text-align: center;
  `,
  title: css`
    font-weight: 700;
    color: ${Colors.gray60};
  `,
  links: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  `,
  link: (isRebrand: boolean) => css`
    ${isRebrand && Typography.paragraph3}
    // Increase hit size.
    display: block;
    padding: ${isRebrand ? '0px' : '3px'};
    margin: ${isRebrand ? '0px 10px' : '0 1px'};
    white-space: nowrap;
    flex: none;
    color: ${isRebrand ? Colors.neutral700 : Colors.gray60};
    cursor: pointer;
    text-decoration: none;
    &:hover,
    &:focus {
      color: ${isRebrand ? Colors.blue : Colors.ocean};
      outline: none;
    }

    ${isRebrand &&
    `@media screen and (max-width: 768px) {
      margin: 0px 6px;
    }`}
  `,
};

// "Letters"
const letters = ['0–9', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

/**
 * @private
 * Renders an alphabetic navigation for browsing apps.
 */
export const AlphabeticAppNav = (props: Props) => {
  const { isRebrand } = useTheme();

  const linkItems = letters.map((letter) => {
    const href = `${props.baseUrl}/${letter.toLowerCase()}`
      .replace('//', '/')
      .replace('–', '-');

    return (
      <li key={letter}>
        <Link
          ariaLabel={`${letter} — Apps beginning with ${letter}`}
          color={null}
          css={Styles.link(isRebrand)}
          href={href}
        >
          {letter}
        </Link>
      </li>
    );
  });

  return (
    <section css={Styles.root}>
      {!isRebrand && <h2 css={Styles.title}>{props.title}</h2>}
      <ul css={Styles.links}>{linkItems}</ul>
    </section>
  );
};

AlphabeticAppNav.defaultProps = {
  baseUrl: '/find-apps/',
  title: 'Apps by Title',
};
